import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './Services.css';

const services = [
  {
    id: 'design',
    image: 'https://images.pexels.com/photos/35673120/pexels-photo-35673120.jpeg',
    title: 'Electronic Product Design',
    short: 'End-to-end embedded hardware and IoT product design.',
    desc: 'From initial concept to production-ready design — our engineers handle system architecture, schematic capture, PCB layout, and regulatory certification for IoT, embedded, and industrial electronics.',
    features: ['Schematic & System Architecture', 'Embedded Hardware Design', 'IoT Device Engineering', 'Regulatory Certification (CE, FCC, BIS)'],
    tag: 'Design',
    accent: '#2563eb',
    accentLight: '#dbeafe',
    accentBorder: '#93c5fd',
    icon: '⚙️',
  },
  {
    id: 'embedded-sw',
    image: 'https://images.pexels.com/photos/18372336/pexels-photo-18372336.jpeg',
    title: 'Embedded Software Development',
    short: 'Firmware, RTOS, and connected device software.',
    desc: 'We develop firmware and application software for microcontrollers, SoCs, and wireless modules — from bare-metal drivers to full RTOS-based applications with cloud connectivity.',
    features: ['Firmware & RTOS Development', 'Device Driver Engineering', 'BLE / Wi-Fi / LoRa Stacks', 'OTA Update Infrastructure'],
    tag: 'Software',
    accent: '#7c3aed',
    accentLight: '#ede9fe',
    accentBorder: '#c4b5fd',
    icon: '💻',
  },
  {
    id: 'pcb',
    image: 'https://images.pexels.com/photos/4779887/pexels-photo-4779887.jpeg',
    title: 'PCB Fabrication',
    short: '2–24 layer PCBs with DFM-validated designs.',
    desc: 'High-quality PCB fabrication from 2 to 24 layers, including rigid, flex, and rigid-flex boards. Every design undergoes DFM analysis, impedance control, and comprehensive electrical testing.',
    features: ['2–24 Layer Rigid, Flex, Rigid-Flex', 'HDI and Microvias', 'Impedance-Controlled Boards', 'Fast Prototype Turnaround (48hr)'],
    tag: 'Fabrication',
    accent: '#0891b2',
    accentLight: '#e0f2fe',
    accentBorder: '#7dd3fc',
    icon: '📋',
  },
  {
    id: 'pcba',
    image: 'https://images.pexels.com/photos/9242900/pexels-photo-9242900.jpeg',
    title: 'PCB Assembly (PCBA)',
    short: 'SMT, THT, and mixed-technology assembly.',
    desc: 'Automated SMT and through-hole assembly using our 2 SMT lines and 5 THT lines. Capable of handling 0201 passives and complex BGA packages with X-ray inspection and full AOI.',
    features: ['SMT & THT Mixed Assembly', 'BGA / μBGA Mounting', 'X-Ray Inspection', 'Automated Optical Inspection (AOI)'],
    tag: 'Assembly',
    accent: '#059669',
    accentLight: '#d1fae5',
    accentBorder: '#6ee7b7',
    icon: '🔧',
  },
  {
    id: 'ems',
    image: 'https://images.pexels.com/photos/19841119/pexels-photo-19841119.jpeg',
    title: 'EMS Solutions (Turnkey)',
    short: 'Full-service manufacturing from 100 to 1M+ units.',
    desc: 'Our end-to-end EMS platform covers component sourcing, manufacturing, quality control, and logistics. One partner. Zero fragmentation. Complete accountability from PO to delivery.',
    features: ['Turnkey BOM Management', 'Consigned & Partial Consign', 'Scalable from NPI to Mass Production', 'Global Delivery & Logistics'],
    tag: 'EMS',
    accent: '#d97706',
    accentLight: '#fef3c7',
    accentBorder: '#fcd34d',
    icon: '🏭',
  },
  {
    id: 'boxbuild',
    image: 'https://images.pexels.com/photos/10699351/pexels-photo-10699351.jpeg',
    title: 'Box Build Assembly',
    short: 'Complete product integration into finished goods.',
    desc: 'We transform PCB assemblies into customer-ready finished products — integrating into custom plastic or metal enclosures, wiring, final testing, and customer-specific packaging.',
    features: ['Enclosure Integration (Plastic & Metal)', 'Wire Harnessing & Cable Assembly', 'Final Functional Testing', 'Custom Packing & Labelling'],
    tag: 'Box Build',
    accent: '#dc2626',
    accentLight: '#fee2e2',
    accentBorder: '#fca5a5',
    icon: '📦',
  },
  {
    id: 'sourcing',
    image: 'https://images.pexels.com/photos/163100/circuit-circuit-board-resistor-computer-163100.jpeg',
    title: 'Component Sourcing',
    short: 'Vetted supply chain with counterfeit prevention.',
    desc: 'We manage your entire component supply chain — sourcing from approved vendors, validating authenticity, managing long lead-time parts, and providing cost-down analysis across your BOM.',
    features: ['Approved Vendor List (AVL) Management', 'Counterfeit Component Prevention', 'Long Lead-Time Part Management', 'BOM Cost Optimization'],
    tag: 'Sourcing',
    accent: '#9333ea',
    accentLight: '#f3e8ff',
    accentBorder: '#d8b4fe',
    icon: '🔗',
  },
  {
    id: 'testing',
    image: 'https://images.pexels.com/photos/12741843/pexels-photo-12741843.jpeg',
    title: 'Testing & Validation',
    short: 'ICT, functional, burn-in, and environmental testing.',
    desc: 'Comprehensive testing services embedded in every production step — from automated optical inspection to in-circuit testing, functional test fixtures, burn-in racks, and environmental stress screening.',
    features: ['In-Circuit Testing (ICT)', 'Functional Test Fixture Design', 'Burn-In & ESS Testing', '100% Traceability & Reporting'],
    tag: 'Testing',
    accent: '#0284c7',
    accentLight: '#e0f2fe',
    accentBorder: '#7dd3fc',
    icon: '🔬',
  },
  {
    id: 'mechanical',
    image: 'https://images.pexels.com/photos/1178498/pexels-photo-1178498.jpeg',
    title: 'Mechanical Design & Enclosures',
    short: 'Custom plastic and sheet metal enclosure design.',
    desc: 'Expert crafting of plastic injection-moulded and sheet metal enclosures — IP-rated for harsh environments. We handle 3D modelling, DFM, tooling liaison, and production sampling.',
    features: ['Plastic Injection Mould Design', 'Sheet Metal Fabrication', 'IP-Rated Environmental Protection', 'DFM and Tooling Liaison'],
    tag: 'Mechanical',
    accent: '#0f766e',
    accentLight: '#ccfbf1',
    accentBorder: '#5eead4',
    icon: '📐',
  },
];

const offerings = [
  { title: 'SMT PCBA', desc: 'Surface mount technology for high-density, miniaturized board assembly.', icon: '⚡', color: '#2563eb' },
  { title: 'THT PCBA', desc: 'Through-hole technology for robust, high-reliability connectors and components.', icon: '🔩', color: '#7c3aed' },
  { title: 'HDI PCBA', desc: 'High-density interconnect for compact IoT, wearable, and medical devices.', icon: '🎯', color: '#0891b2' },
  { title: 'Rigid/Flex PCB', desc: 'Rigid-flex solutions for 3D packaging, wearables, and aerospace applications.', icon: '🔄', color: '#059669' },
];

function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('revealed'); observer.unobserve(e.target); }
      }),
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

export default function Services() {
  useScrollReveal();

  return (
    <main className="services-page page-wrapper">

      {/* ── HERO ── */}
      <section className="svc-hero">
        <div className="svc-hero__grid" />
        <div className="svc-hero__orbs">
          <div className="orb orb--1" />
          <div className="orb orb--2" />
          <div className="orb orb--3" />
        </div>
        <div className="container svc-hero__inner">
          <div className="hero-badge reveal">Our Services</div>
          <h1 className="svc-hero__title reveal">Complete Electronics<br /><span>Engineering Stack</span></h1>
          <p className="svc-hero__sub reveal">
            From your first prototype to mass production — every service you need, under one roof.
          </p>
          <div className="svc-hero__pills reveal">
            {['Design', 'Firmware', 'PCB Fab', 'PCBA', 'EMS', 'Box Build', 'Sourcing', 'Testing'].map((t, i) => (
              <a key={t} href={`#${services[i]?.id || ''}`} className="pill">{t}</a>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICE BLOCKS ── */}
      <section className="section svc-list-section">
        <div className="container">
          {services.map((svc, i) => (
            <div
              key={svc.id}
              id={svc.id}
              className={`svc-row ${i % 2 !== 0 ? 'svc-row--reverse' : ''} reveal`}
              style={{ '--accent': svc.accent, '--accent-light': svc.accentLight, '--accent-border': svc.accentBorder, '--delay': `${Math.min(i * 0.05, 0.2)}s` }}
            >
              {/* Image side */}
              <div className="svc-img-wrap">
                <div className="svc-img-frame">
                  <img src={svc.image} alt={svc.title} loading="lazy" />
                  <div className="svc-img-overlay" />
                  <span className="svc-img-tag">{svc.icon} {svc.tag}</span>
                </div>
                <div className="svc-accent-blob" />
              </div>

              {/* Content side */}
              <div className="svc-content">
                <div className="svc-tag-pill">{svc.tag}</div>
                <h2 className="svc-title">{svc.title}</h2>
                <p className="svc-desc">{svc.desc}</p>
                <ul className="svc-feat-list">
                  {svc.features.map(f => (
                    <li key={f}>
                      <span className="feat-dot" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className="svc-btn">Get a Quote →</Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── TECHNOLOGY OFFERINGS ── */}
      <section className="section offerings-section">
        <div className="container">
          <div className="section-center reveal">
            <div className="off-tag">Technology Offerings</div>
            <h2 className="off-title">Assembly <span>Technologies</span></h2>
          </div>
          <div className="off-grid">
            {offerings.map((o, i) => (
              <div key={o.title} className="off-card reveal" style={{ '--color': o.color, '--delay': `${i * 0.1}s` }}>
                <div className="off-icon">{o.icon}</div>
                <h3>{o.title}</h3>
                <p>{o.desc}</p>
                <div className="off-bar" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY OUR SERVICES ── */}
      <section className="section why-section">
        <div className="container">
          <div className="why-inner">
            <div className="why-left reveal slide-left">
              <div className="why-tag">Why Our Services</div>
              <h2 className="why-title">A Platform Built for <span>Reliability</span></h2>
              <p className="why-desc">
                Zxeon FS is not just a contract manufacturer. We are a full-stack electronics platform — design, manufacturing, testing, and logistics all managed through a single accountable team.
              </p>
              <ul className="why-checklist">
                {[
                  'Single point of contact for design and manufacturing',
                  'DFM-reviewed every PCB before fabrication',
                  'Industry-standard quality systems (ISO 9001)',
                  'Full BOM traceability from component to finished product',
                ].map((item, i) => (
                  <li key={i} className="why-check-item" style={{ '--delay': `${i * 0.1}s` }}>
                    <span className="why-check">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="why-btn">Discuss Your Project →</Link>
            </div>
            <div className="why-right reveal slide-right">
              {[
                { n: '200+', l: 'Products Built', color: '#2563eb', bg: '#dbeafe' },
                { n: '98.7%', l: 'First-Pass Yield', color: '#059669', bg: '#d1fae5' },
                { n: '48hr', l: 'Prototype Turnaround', color: '#7c3aed', bg: '#ede9fe' },
                { n: '24×7', l: 'Production Monitoring', color: '#d97706', bg: '#fef3c7' },
              ].map((s, i) => (
                <div key={s.l} className="why-stat" style={{ '--color': s.color, '--bg': s.bg, '--delay': `${i * 0.12}s` }}>
                  <strong>{s.n}</strong>
                  <span>{s.l}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="svc-cta">
        <div className="svc-cta__mesh" />
        <div className="container svc-cta__inner reveal">
          <div>
            <h2>Have a Project in Mind?<br /><span>Let's Talk.</span></h2>
            <p>Send us your BOM, Gerber files, or just a product brief. We'll respond within 24 hours.</p>
          </div>
          <div className="svc-cta__actions">
            <Link to="/contact" className="btn-glow-large">Upload Files & Get a Quote →</Link>
            <Link to="/case-studies" className="btn-ghost-large">See Case Studies</Link>
          </div>
        </div>
      </section>

    </main>
  );
}